import { AlertDuration, AlertType, useAlertManager, useClassFactory } from "@pasta/box-v2";
import { computed, ref } from "vue";
import dayjs from "dayjs";
import { isNil } from "lodash";
import fixWebMDuration from "webm-duration-fix";
import { formatTime } from "../utils";
import { useSimpleFeatureStore } from "../../app/stores/simple-features.store";

export class AudioRecorder {
  private mimeType = 'audio/webm; codecs="opus"';
  private audioBitsPerSecond = 32_000;

  get alertManager() {
    return useAlertManager();
  }

  public state = ref<"inactive" | "paused" | "recording">("inactive");

  public recordTime = ref<string>("00:00");
  private timer: number | null = null;
  private startedAt: dayjs.Dayjs | null = null;
  private elapsed: number = 0;
  private totalElapsed: number = 0;

  private stream: MediaStream | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private chunks: Array<string | Blob> = [];

  public supportsMedia = computed(() => !!navigator.mediaDevices && MediaRecorder.isTypeSupported(this.mimeType));

  public analyser: AnalyserNode | null = null;
  public analyserDataArray: Uint8Array<ArrayBuffer> = new Uint8Array();
  public analyserBufferLength: number = 0;

  public get duration() {
    return this.totalElapsed + this.elapsed;
  }

  public get maxDuration() {
    return useSimpleFeatureStore().privateMessage.audio.maxDuration;
  }

  public reachedMaxDuration = ref(false);

  async start() {
    try {
      this.stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      this.record(this.stream);
      this.startTimer();
      this.getAnalyser();
    } catch (e) {
      let message = "Une erreur s'est produite lors du démarrage de l'enregistrement";
      if ((e as DOMException).name === "NotAllowedError") {
        message = "L'accès au microphone a été refusé, veuillez autoriser le parapheur a accéder au microphone.";
      }
      this.alertManager.createAlert({
        type: AlertType.ERROR,
        duration: AlertDuration.INFINITE,
        message,
      });
    }
  }

  startTimer() {
    this.startedAt = dayjs();
    this.timer = window.setInterval(() => {
      if (isNil(this.startedAt)) return;
      this.elapsed = dayjs().diff(this.startedAt);
      this.recordTime.value = formatTime(this.duration);
      if (!this.reachedMaxDuration.value && this.duration >= this.maxDuration) {
        this.reachedMaxDuration.value = true;
        this.pause();
      }
    }, 20);
  }

  setState(_ev: Event) {
    this.state.value = this.mediaRecorder?.state || "inactive";
  }

  record(stream: MediaStream) {
    this.mediaRecorder = new MediaRecorder(stream, {
      mimeType: this.mimeType,
      audioBitsPerSecond: this.audioBitsPerSecond,
    });

    this.mediaRecorder.onstart = this.setState.bind(this);
    this.mediaRecorder.onpause = this.setState.bind(this);
    this.mediaRecorder.onstop = this.setState.bind(this);
    this.mediaRecorder.onresume = this.setState.bind(this);

    this.mediaRecorder.onerror = event => {
      this.setState(event);
      console.error(event);
      this.alertManager.createAlert({
        type: AlertType.ERROR,
        duration: AlertDuration.INFINITE,
        message: "Une erreur s'est produite durant l'enregistrement",
      });
      this.cleanup();
    };

    this.mediaRecorder.ondataavailable = (ev: BlobEvent) => {
      this.chunks.push(ev.data);
    };

    this.mediaRecorder.start();
  }

  getAnalyser() {
    if (!this.stream) throw new Error("Cannot analyze, no available stream");

    const audioCtx = new AudioContext();
    const source = audioCtx.createMediaStreamSource(this.stream);
    this.analyser = audioCtx.createAnalyser();
    this.analyser.fftSize = 2048;
    this.analyserBufferLength = this.analyser.frequencyBinCount;
    this.analyserDataArray = new Uint8Array(this.analyserBufferLength);

    source.connect(this.analyser);
  }

  pause() {
    this.startedAt = null;
    this.totalElapsed += this.elapsed;
    this.mediaRecorder!.pause();
  }

  resume() {
    this.startedAt = dayjs();
    this.mediaRecorder!.resume();
  }

  async getCurrentData(): Promise<Blob> {
    return new Promise(resolve => {
      this.mediaRecorder!.ondataavailable = async (ev: BlobEvent) => {
        this.chunks.push(ev.data);
        const blob = new Blob(this.chunks, {
          type: this.mediaRecorder!.mimeType,
        });
        resolve(this.mimeType.includes("webm") ? await fixWebMDuration(blob) : blob);
        this.mediaRecorder!.ondataavailable = (ev: BlobEvent) => this.chunks.push(ev.data);
      };
      this.mediaRecorder!.requestData();
    });
  }

  cleanup() {
    this.analyser?.disconnect();
    this.analyser = null;
    this.analyserDataArray = new Uint8Array();
    this.analyserBufferLength = 0;
    if (this.mediaRecorder) {
      this.mediaRecorder.ondataavailable = () => null;
      this.mediaRecorder.stop();
      this.mediaRecorder.stream.getTracks().forEach(track => track.stop());
      this.mediaRecorder = null;
    }
    this.stream = null;
    if (!isNil(this.timer)) {
      window.clearInterval(this.timer);
    }
    this.startedAt = null;
    this.elapsed = 0;
    this.totalElapsed = 0;
    this.recordTime.value = "00:00";
    this.chunks = [];
    this.reachedMaxDuration.value = false;
  }

  async end() {
    await new Promise(resolve => {
      this.mediaRecorder!.stop();
      this.mediaRecorder!.ondataavailable = (ev: BlobEvent) => {
        this.chunks.push(ev.data);
        resolve(1);
      };
    });
    const blob = new Blob(this.chunks, { type: this.mediaRecorder!.mimeType });
    const duration = this.duration;
    this.cleanup();

    return {
      data: this.mimeType.includes("webm") ? await fixWebMDuration(blob) : blob,
      duration,
    };
  }
}

export const [audioRecorderToken, useAudioRecorder] = useClassFactory(AudioRecorder);
